import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Input, Tooltip } from 'antd';
import debounce from 'lodash.debounce';
import './InputField.scss';

const { TextArea } = Input;

const InputField = props => {
  const HAS_NOT_DEFAULT_VALUE = false;
  const [isValueValid, setIsValueValid] = useState(true);
  const firstRender = useRef(true);

  const {
    title,
    defaultValue,
    titlePosition,
    className,
    placeholder,
    onChange,
    disabled,
    tooltip,
    isRequired,
    validationError,
    messageInfo,
    validationRegex,
    boxClassName,
    resetField,
    onValidationError,
    width
  } = props;
  const [inputValue, setInputValue] = useState(null);

  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
    };
  }, []);

  useEffect(() => {
    if (props.initData && props.onChange && props.setDefault) {
      props.onChange(props.initData);
    }
  }, [props.initData, props.setDefault]);

  useEffect(() => {
    if (props.fieldValue) {
      props.onChange(props.fieldValue);
      setInputValue(props.fieldValue);
    }
  }, [props.fieldValue]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      if (!!defaultValue) {
        setInputValue(defaultValue);
      }
      return;
    }
  }, [firstRender]);

  useEffect(() => {
    if (props.hasOwnProperty('updatedValue')) setInputValue(props.updatedValue);
  }, [props.updatedValue]);

  const getTitle = () => {
    if (title) {
      return <div className="select-field-title">{title}</div>;
    }
  };

  const onFieldChange = (inputValue, _validationRegex) => {
    if (!new RegExp(_validationRegex).test(inputValue)) {
      setIsValueValid(false);

      if (onValidationError) {
        onValidationError(true);
      }
    } else {
      setIsValueValid(true);

      if (onChange) {
        onChange(inputValue);
      }
      if (onValidationError) {
        onValidationError(false);
      }
    }
  };

  const debouncedChangeHandler = useMemo(
    () =>
      debounce(
        (value, _validationRegex) => onFieldChange(value, _validationRegex),
        250
      ),
    []
  );

  const generateInputFieldProps = () => {
    let selectProps = {
      className: `inputField ${titlePosition === 'left' ? 'inputSmall' : ''}`,
      onChange: e => {
        setInputValue(e.target.value);
        debouncedChangeHandler(e.target.value, validationRegex);
      },
      disabled: disabled ? disabled : false,
      maxLength: props.maxLength,
      allowClear: props.allowClear,
      value: inputValue,
      readOnly: false,
    };
    if (props.showCount) {
      selectProps.showCount = props.showCount;
    }
    if (placeholder) {
      selectProps.placeholder = placeholder;
    }
    if (props.initData) {
      selectProps.placeholder = props.initData
        ? props.initData
        : props.placeholder
          ? props.placeholder
          : '';
    }
    if (props.fieldValue) {
      selectProps.placeholder = props.fieldValue
        ? props.fieldValue
        : props.placeholder
          ? props.placeholder
          : '';
    }
    if (props.readOnly) {
      selectProps.readOnly = true;
    }
    if (resetField) {
      selectProps.value = undefined;
    }
    return selectProps;
  };

  return (
    !props.hide && (
      <div
        className={`inputFieldArea ${titlePosition === 'left' ? 'withLeftTitle' : ''} ${className}`}
        style={{width: width}}
      >
        <Tooltip placement="topLeft" title={tooltip}>
          {getTitle()}
        </Tooltip>
        <div
          className={`${props.inputBox ? props.inputBox : 'inputBox'} ${boxClassName ? boxClassName : ''}`}
        >
          {props.textArea ? (
            <TextArea {...generateInputFieldProps()} />
          ) : (
            <Input {...generateInputFieldProps()} />
          )}
          {!isValueValid && validationError && (
            <div className="field-error">{validationError}</div>
          )}
          {messageInfo && <div>{messageInfo}</div>}
        </div>
      </div>
    )
  );
};

export default React.memo(InputField);
